import React from 'react';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import useSupplierStatusForm from './hooks/useSupplierStatusForm';
import FormPartial from '../../components/form/formContainer/FormPartial';
import TextInput from '../../components/form/formInputs/TextInput/TextInput';
import NumericInput from '../../components/form/formInputs/NumericInput/NumericInput';
import BaseForm, { ParamTypes } from '../../components/form/baseForm/BaseForm';
import { NumberMaxValues, RoutingValues } from '../../types/Shared.types';
import TranslatableText from '../../components/i18n/TranslatableText';
import { SupplierStatusFormDefs } from '../../constants/i18n/translations/termDefinitions/supply-chain';
import { SupplierAddressStatusRevisionHistoryFields } from '../../constants/revisionHistory/supplierApplicationRevisionHistory';

const SupplierStatusForm = () => {
    const { id } = useParams<ParamTypes>();
    const {
        fields,
        termSet,
        auditableFields,
        formMethods,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        handleFieldChange,
        isLoadingForm,
        canCreate,
        canUpdate,
        canDelete,
    } = useSupplierStatusForm(id);

    return (
        <BaseForm
            title={
                <TranslatableText
                    termSet={termSet}
                    termKey={SupplierStatusFormDefs.Title}
                />
            }
            formMethods={formMethods}
            isLoading={isLoadingForm}
            isNew={id === RoutingValues.newId}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onCancel={closeForm}
            displayCreateButton={canCreate}
            displayUpdateButton={canUpdate}
            displayDeleteButton={canDelete}
            showOnRevision={true}
            fieldMappings={SupplierAddressStatusRevisionHistoryFields}
            entityUuid={fields?.uuid}
            auditableFields={auditableFields}>
            <FormPartial>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierStatusFormDefs.Code}
                                />
                            }
                            id="code"
                            value={fields.code}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxLength={30}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <NumericInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierStatusFormDefs.SortOrder}
                                />
                            }
                            id="sortOrder"
                            value={fields.sortOrder}
                            disabled={true}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxValue={NumberMaxValues.smallint}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierStatusFormDefs.Description}
                                />
                            }
                            id="description"
                            value={fields.description}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            maxLength={255}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        SupplierStatusFormDefs.StatusActive
                                    }
                                />
                            }
                            control={
                                <Checkbox
                                    id="statusActive"
                                    checked={fields.statusActive}
                                    onChange={handleFieldChange}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        SupplierStatusFormDefs.SupplierActive
                                    }
                                />
                            }
                            control={
                                <Checkbox
                                    id="supplierActive"
                                    checked={fields.supplierActive}
                                    onChange={handleFieldChange}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierStatusFormDefs.Default}
                                />
                            }
                            control={
                                <Checkbox
                                    id="default"
                                    checked={fields.default}
                                    onChange={handleFieldChange}
                                />
                            }
                        />
                    </Grid>
                </Grid>
            </FormPartial>
        </BaseForm>
    );
};

export default SupplierStatusForm;
